import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, QrCode, BarChart3, History, Key, Wallet, Search, Check, DollarSign, ChevronRight, HelpCircle } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { useAuth, API } from "../../context/AuthContext";
import { PasswordInput } from "../../components/shared/PasswordInput";

// Scan Wallet Component
const ScanCoupon = () => {
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [mode, setMode] = useState("manual");
  const [code, setCode] = useState("");
  const [verifiedWallet, setVerifiedWallet] = useState(null);
  const [transactionAmount, setTransactionAmount] = useState("");
  const [scanning, setScanning] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => {
    const fetchEvents = async () => {
      try { const res = await authAxios.get("/wallet/merchant/active-events"); setActiveEvents(res.data); }
      catch { setActiveEvents([]); }
      finally { setEventsLoading(false); }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    let html5Qrcode = null;
    if (mode === "camera" && !scanning && selectedEvent) {
      setScanning(true);
      html5Qrcode = new Html5Qrcode("qr-reader");
      html5Qrcode.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => { html5Qrcode.stop().then(() => { setCode(decodedText); setMode("manual"); setScanning(false); handleVerifyWallet(decodedText); }).catch(() => {}); }, () => {}
      ).catch(() => { toast.error("Camera access failed"); setMode("manual"); setScanning(false); });
    }
    return () => { if (html5Qrcode && html5Qrcode.isScanning) html5Qrcode.stop().catch(() => {}); setScanning(false); };
  }, [mode, selectedEvent]);

  const handleVerifyWallet = async (walletId = code) => {
    if (!walletId) return toast.error("Enter wallet or coupon code");
    // Try wallet first; if not found, fall back to coupon lookup. The scanner
    // has one input box for both — user just scans/types and we figure out
    // whether it's a wallet QR or a coupon code.
    try {
      const res = await authAxios.get(`/wallet/balance/${walletId}`);
      setVerifiedWallet(res.data);
      setTransactionAmount("");
      return;
    } catch (walletErr) {
      const status = walletErr.response?.status;
      // Only fall through when it's a "not found" on wallet side; other errors surface as-is
      if (status !== 404) {
        toast.error(walletErr.response?.data?.detail || "Wallet lookup failed");
        return;
      }
    }
    // Coupon fallback — coupon endpoint gives us the same shape (balance +
    // event scoping) so we can reuse the verified-wallet UI.
    try {
      const res = await authAxios.get(`/coupons/verify/${walletId}`, {
        params: selectedEvent ? { event_id: selectedEvent.id } : {},
      });
      // Backend returns { coupon: {...}, event: {...} } — flatten for the UI.
      const c = res.data.coupon || {};
      const ev = res.data.event || {};
      setVerifiedWallet({
        isCoupon: true,
        coupon_code: c.code,
        customer_name: c.issued_to_email || "Coupon holder",
        customer_id: c.id,
        balance: c.balance,
        expiry_date: c.expiry_date,
        event_id: c.event_id,
        event_name: ev.name || c.event_name,
        valid_dates: c.valid_dates,
        source: c.source,
      });
      setTransactionAmount("");
    } catch (couponErr) {
      // Surface the coupon-side reason (invalid, wrong day, wrong event, expired…)
      toast.error(couponErr.response?.data?.detail || "Coupon or wallet not found");
    }
  };

  const handleWalletSpend = async () => {
    if (!transactionAmount || parseFloat(transactionAmount) <= 0) return toast.error("Enter amount to charge");
    const amt = parseFloat(transactionAmount);
    // Coupon spend path — mirrors wallet-spend UX. `/coupons/redeem` returns
    // remaining balance so we surface the same "Charged / Remaining" toast.
    if (verifiedWallet?.isCoupon) {
      if (amt > verifiedWallet.balance) return toast.error(`Insufficient balance. Available: \u20ac${verifiedWallet.balance.toFixed(2)}`);
      try {
        const res = await authAxios.post("/coupons/redeem", {
          coupon_code: verifiedWallet.coupon_code,
          amount: amt,
          event_id: selectedEvent?.id,
        });
        toast.success(`\u20ac${amt.toFixed(2)} charged! Remaining: \u20ac${(res.data.remaining_balance ?? res.data.new_balance ?? 0).toFixed(2)}`);
        setVerifiedWallet(null); setCode(""); setTransactionAmount("");
      } catch (e) { toast.error(e.response?.data?.detail || "Coupon redemption failed"); }
      return;
    }
    if (amt > verifiedWallet.wallet.total_balance) return toast.error(`Insufficient balance. Available: \u20ac${verifiedWallet.wallet.total_balance.toFixed(2)}`);
    try {
      const res = await authAxios.post("/wallet/spend", { customer_id: verifiedWallet.customer.customer_id, amount: amt, event_id: selectedEvent?.id });
      toast.success(`\u20ac${amt.toFixed(2)} charged! Remaining: \u20ac${res.data.remaining_balance.toFixed(2)}`);
      setVerifiedWallet(null); setCode(""); setTransactionAmount("");
    } catch (e) { toast.error(e.response?.data?.detail || "Transaction failed"); }
  };

  if (eventsLoading) return <div className="text-center py-8 text-slate-400">Loading events...</div>;

  // No active events
  if (activeEvents.length === 0) {
    return (
      <div className="text-center py-12" data-testid="no-events">
        <Wallet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-bold text-slate-500 mb-2">No Active Events</h2>
        <p className="text-slate-400 text-sm">You have no active events to process sales.</p>
      </div>
    );
  }

  // Event selection
  if (!selectedEvent) {
    return (
      <div data-testid="event-select">
        <h1 className="text-xl font-bold text-[#1e3a5f] mb-4">Select Event</h1>
        <div className="space-y-3">
          {activeEvents.map(ev => (
            <button key={ev.id} onClick={() => setSelectedEvent(ev)} className="w-full bg-white rounded-2xl p-4 shadow-sm text-left hover:shadow-md transition-shadow" data-testid={`event-${ev.id}`}>
              <h3 className="font-bold text-[#1e3a5f]">{ev.name}</h3>
              <p className="text-slate-500 text-sm">{ev.start_date} - {ev.end_date}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (verifiedWallet) {
    // Normalized data — coupons and wallets funnel into the same layout so
    // the merchant sees the same "verified → enter amount → charge" flow.
    const isCoupon = !!verifiedWallet.isCoupon;
    const displayName = isCoupon ? verifiedWallet.customer_name : verifiedWallet.customer.name;
    const displayId = isCoupon ? verifiedWallet.coupon_code : verifiedWallet.customer.customer_id;
    const balance = isCoupon ? verifiedWallet.balance : verifiedWallet.wallet.total_balance;
    return (
      <div className="text-center py-8 animate-scaleIn" data-testid="wallet-verified">
        <p className="text-xs text-slate-400 mb-2">{selectedEvent.name}</p>
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"><Wallet className="w-10 h-10 text-green-500" /></div>
        {isCoupon && <p className="text-[10px] uppercase tracking-wider text-amber-600 font-bold mb-1" data-testid="verified-coupon-badge">Coupon</p>}
        <h2 className="text-xl font-bold mb-1">{displayName}</h2>
        <p className="text-slate-500 font-mono mb-4">{displayId}</p>
        <p className="text-3xl font-bold text-[#2d8a4e] mb-6">Balance: {"\u20ac"}{balance.toFixed(2)}</p>
        <div className="mb-4">
          <label className="text-sm text-slate-500">Amount to Charge *</label>
          <div className="relative mt-1"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">{"\u20ac"}</span><input type="number" step="0.01" max={balance} value={transactionAmount} onChange={e => setTransactionAmount(e.target.value)} placeholder="0.00" className="w-full p-3 pl-8 rounded-xl border text-center text-xl" data-testid="wallet-amount" /></div>
          <p className="text-slate-400 text-xs mt-1">Max: {"\u20ac"}{balance.toFixed(2)}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { setVerifiedWallet(null); setCode(""); }} className="flex-1 py-3 rounded-full border-2 border-slate-200 font-bold" data-testid="cancel-wallet">Cancel</button>
          <button onClick={handleWalletSpend} disabled={!transactionAmount || parseFloat(transactionAmount) <= 0} className="flex-1 py-3 rounded-full bg-[#2d8a4e] text-white font-bold disabled:opacity-50" data-testid="confirm-wallet">Charge {"\u20ac"}{transactionAmount || "0"}</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="scan-wallet">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#1e3a5f]">Scan Wallet</h1>
          <p className="text-slate-500 text-sm">{selectedEvent.name}</p>
        </div>
        <button onClick={() => setSelectedEvent(null)} className="text-sm text-slate-500 hover:underline">Change Event</button>
      </div>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setMode("manual")} className={`flex-1 py-2 rounded-full font-medium ${mode === "manual" ? "bg-[#2d8a4e] text-white" : "bg-slate-100"}`} data-testid="mode-manual">Manual Entry</button>
        <button onClick={() => setMode("camera")} className={`flex-1 py-2 rounded-full font-medium ${mode === "camera" ? "bg-[#2d8a4e] text-white" : "bg-slate-100"}`} data-testid="mode-camera">Scan QR</button>
      </div>
      {mode === "manual" ? (
        <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4"><Wallet className="w-8 h-8 text-blue-600" /></div>
          <h2 className="font-bold mb-4">Enter Wallet ID</h2>
          <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} maxLength={14} placeholder="MM-26-XXXXXX or MG-26-XXXXXX" className="w-full p-4 rounded-xl border text-center text-lg font-mono tracking-wider uppercase mb-4" data-testid="code-input" />
          <button onClick={() => handleVerifyWallet()} className="w-full py-3 rounded-full bg-[#1e3a5f] text-white font-bold flex items-center justify-center gap-2" data-testid="verify-btn"><Search className="w-5 h-5" /> Find Account</button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-4 shadow-sm"><div id="qr-reader" className="w-full aspect-square max-w-sm mx-auto rounded-xl overflow-hidden" /><p className="text-center text-slate-500 mt-3 text-sm">Point camera at wallet QR code</p></div>
      )}
    </div>
  );
};

// Merchant History Component
const MerchantHistory = () => {
  const [data, setData] = useState(null);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const { authAxios } = useAuth();

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => { try { const res = await authAxios.get("/analytics/merchant"); setData(res.data); } catch (e) { toast.error("Failed to load data"); } };

  if (!data) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  return (
    <div data-testid="merchant-history">
      <h1 className="text-xl font-bold text-[#1e3a5f] mb-6">Dashboard</h1>
      <div className="bg-[#2d8a4e] rounded-2xl p-4 mb-6 text-white">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><p className="text-lg sm:text-2xl font-bold">{"\u20ac"}{data.total_sales.toFixed(2)}</p><p className="text-xs sm:text-sm opacity-80">Total Sales</p></div>
          <div><p className="text-lg sm:text-2xl font-bold">{"\u20ac"}{data.total_payout.toFixed(2)}</p><p className="text-xs sm:text-sm opacity-80">Payout</p></div>
          <div><p className="text-lg sm:text-2xl font-bold text-amber-300">{"\u20ac"}{data.total_pending.toFixed(2)}</p><p className="text-xs sm:text-sm opacity-80">Pending</p></div>
        </div>
      </div>
      <h2 className="font-bold mb-3">Event Performance</h2>
      <div className="space-y-3 mb-6">
        {data.sales_by_event.length === 0 ? (<p className="text-center text-slate-400 py-4">No events yet</p>) :
          data.sales_by_event.map((e, i) => {
            const isExpanded = expandedEvent === e.event_name;
            const eventTxns = data.history.filter(t => t.event_name === e.event_name);
            return (
              <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <button onClick={() => setExpandedEvent(isExpanded ? null : e.event_name)} className="w-full p-4 text-left">
                  <div className="flex justify-between items-center mb-2"><h3 className="font-bold">{e.event_name}</h3><ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} /></div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div><p className="text-slate-500">Sales</p><p className="font-bold">{"\u20ac"}{e.sales.toFixed(2)}</p></div>
                    <div><p className="text-slate-500">Margin ({e.margin_pct}%)</p><p className="font-bold text-amber-600">{"\u20ac"}{e.margin.toFixed(2)}</p></div>
                    <div><p className="text-slate-500">Payout</p><p className="font-bold text-green-600">{"\u20ac"}{e.payout.toFixed(2)}</p></div>
                    <div><p className="text-slate-500">Pending</p><p className="font-bold text-red-500">{"\u20ac"}{e.pending.toFixed(2)}</p></div>
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t px-4 pb-4"><p className="text-sm font-medium text-slate-500 py-2">Transactions</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {eventTxns.length === 0 ? (<p className="text-slate-400 text-sm text-center py-2">No transactions</p>) :
                        eventTxns.map(t => (<div key={t.id} className="flex justify-between items-center py-2 border-b last:border-0"><div>{t.type === "payout" ? <p className="text-sm font-medium text-blue-600">Payout Settled</p> : <p className="font-mono text-sm">{t.coupon_code}</p>}<p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}</p></div><p className={`font-bold ${t.type === "payout" ? "text-blue-600" : "text-green-600"}`}>{t.type === "payout" ? "-" : "+"}{"\u20ac"}{t.amount.toFixed(2)}</p></div>))
                      }
                    </div>
                  </div>
                )}
              </div>
            );
          })
        }
      </div>
    </div>
  );
};

// Merchant Transactions Component
const MerchantTransactions = () => {
  const [data, setData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { authAxios } = useAuth();

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => { try { const res = await authAxios.get("/analytics/merchant"); setData(res.data); } catch (e) { toast.error("Failed to load data"); } };

  if (!data) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  const filteredHistory = searchQuery.trim() ? data.history.filter(t => {
    const q = searchQuery.toLowerCase();
    return (t.coupon_code || "").toLowerCase().includes(q) || (t.event_name || "").toLowerCase().includes(q) || new Date(t.created_at).toLocaleString().toLowerCase().includes(q) || (t.type || "").toLowerCase().includes(q) || `\u20ac${t.amount.toFixed(2)}`.includes(q);
  }) : data.history;

  return (
    <div data-testid="merchant-transactions">
      <h1 className="text-xl font-bold text-[#1e3a5f] mb-4">All Transactions ({data.transaction_count})</h1>
      <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search date, event, coupon..." className="w-full p-3 rounded-xl border mb-4 bg-white" data-testid="transaction-search" />
      <div className="space-y-2">
        {filteredHistory.length === 0 ? (<p className="text-center text-slate-400 py-4">{searchQuery ? "No matching transactions" : "No transactions yet"}</p>) :
          filteredHistory.map(t => (
            <div key={t.id} className="bg-white rounded-xl p-3 shadow-sm flex justify-between items-center">
              <div><p className={`font-bold ${t.type === "payout" ? "text-blue-600" : "text-green-600"}`}>{t.type === "payout" ? "-" : "+"}{"\u20ac"}{t.amount.toFixed(2)}</p><p className="text-slate-400 text-xs">{new Date(t.created_at).toLocaleString()}</p>{t.type === "payout" ? <p className="text-blue-500 text-xs font-medium">Payout - {t.event_name}</p> : <p className="text-slate-500 text-xs font-mono">{t.coupon_code}</p>}</div>
              {t.type === "payout" ? <DollarSign className="w-5 h-5 text-blue-500" /> : <Check className="w-5 h-5 text-green-500" />}
            </div>
          ))
        }
      </div>
    </div>
  );
};

// Merchant Settings Component
const MerchantSettings = ({ onLogout }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { authAxios, logout } = useAuth();
  const navigate = useNavigate();

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) return toast.error("Fill all fields");
    if (newPassword !== confirmPassword) return toast.error("Passwords don't match");
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters");
    try { await authAxios.post("/merchant/change-password", { current_password: currentPassword, new_password: newPassword }); toast.success("Password changed! Please login again."); logout(); navigate("/" + (window.location.pathname.split("/")[1] || "mymaitree") + "/admin"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="merchant-settings">
      <h1 className="text-xl font-bold text-[#1e3a5f] mb-6">Change Password</h1>
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="space-y-4">
          <PasswordInput value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Current Password" testId="current-password" />
          <PasswordInput value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New Password" testId="new-password" />
          <PasswordInput value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm Password" testId="confirm-password" />
        </div>
        <button onClick={handleChangePassword} className="w-full py-3 rounded-full bg-[#1e3a5f] text-white font-bold mt-6" data-testid="change-password-btn">Change Password</button>
      </div>
    </div>
  );
};

// Main Merchant Dashboard
export default function MerchantDashboard() {
  const [view, setView] = useState("scan");
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (!user || user.role !== "merchant") navigate("/" + (window.location.pathname.split("/")[1] || "mymaitree") + "/admin"); }, [user]);

  const handleLogout = async () => {
    // iter139: land on the tenant admin login, not the root splash.
    const slug = window.location.pathname.split("/")[1] || "mymaitree";
    try { await axios.post(`${API}/merchant/logout`, {}, { withCredentials: true }); } catch (e) {}
    logout(); navigate(`/${slug}/admin`);
  };

  return (
    <div className="min-h-screen" data-testid="merchant-dashboard">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src="/mymaitree-logo.png" alt="My Maitree" className="h-8 w-auto" onError={(e) => { e.target.style.display = 'none'; }} /><div><p className="text-slate-400 text-xs">Merchant</p><h1 className="text-sm font-bold text-[#1e3a5f]">{user?.name || "Merchant"}</h1></div></div>
          <div className="flex items-center gap-2">
            <button onClick={() => setView("settings")} className={`p-2 rounded-full hover:bg-slate-100 ${view === "settings" ? "text-[#2d8a4e]" : "text-slate-400"}`} data-testid="header-settings-btn"><Key className="w-5 h-5" /></button>
            <button onClick={() => navigate("/help?role=merchant")} className="p-2 rounded-full hover:bg-slate-100 text-slate-400" data-testid="help-btn"><HelpCircle className="w-5 h-5" /></button>
            <button onClick={handleLogout} className="p-2 rounded-full hover:bg-red-50 text-red-500" data-testid="header-logout-btn"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        {view !== "settings" && (
          <div className="max-w-lg mx-auto flex border-t">
            <button onClick={() => setView("scan")} className={`flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium border-b-2 ${view === "scan" ? "border-[#2d8a4e] text-[#2d8a4e]" : "border-transparent text-slate-400"}`} data-testid="nav-scan"><QrCode className="w-4 h-4" /> Scan</button>
            <button onClick={() => setView("history")} className={`flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium border-b-2 ${view === "history" ? "border-[#2d8a4e] text-[#2d8a4e]" : "border-transparent text-slate-400"}`} data-testid="nav-history"><BarChart3 className="w-4 h-4" /> Dashboard</button>
            <button onClick={() => setView("transactions")} className={`flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium border-b-2 ${view === "transactions" ? "border-[#2d8a4e] text-[#2d8a4e]" : "border-transparent text-slate-400"}`} data-testid="nav-transactions"><History className="w-4 h-4" /> Transactions</button>
          </div>
        )}
      </div>
      <div className="max-w-lg mx-auto p-6">
        {view === "scan" && <ScanCoupon />}
        {view === "history" && <MerchantHistory />}
        {view === "transactions" && <MerchantTransactions />}
        {view === "settings" && <MerchantSettings onLogout={handleLogout} />}
      </div>
    </div>
  );
}
